export type ProviderCode = 'tabby' | 'tamara' | 'cashew';

export type ProviderConfig = {
  code: ProviderCode;
  name: string;
  tenors: number[];                // e.g., [3,6,12]
  aprRange?: [number, number];     // optional APR band if disclosed
  enabled: boolean;
  currency?: string;               // default 'AED'
  logoUrl?: string;                // optional UI use
  supportsPrecheck?: boolean;      // if provider offers soft eligibility check
};

export type CreateCheckoutPayload = {
  amountAED: number;
  customer: { name: string; email: string; phone?: string };
  metadata?: Record<string, any>;  // include listingId, leadId, utm, etc.
  returnUrl: string;               // where the provider should redirect back
};

export type ProviderAdapter = {
  getConfig: () => Promise<ProviderConfig>;
  createCheckoutLink: (payload: CreateCheckoutPayload) => Promise<{ url: string }>;
  verifyWebhook?: (req: Request) => Promise<{ ok: boolean; event?: string; data?: any }>;
};

/**
 * Formal BNPLProvider interface.
 * Every provider file (tabby.ts, tamara.ts, cashew.ts) must implement this.
 */
export interface BNPLProvider {
  /** Returns static provider configuration */
  getConfig(): Promise<ProviderConfig>;

  /** Initiates a checkout session and returns a redirect URL */
  initiateCheckout(payload: CreateCheckoutPayload): Promise<{ url: string }>;

  /** Verifies an incoming webhook signature and extracts event data */
  verifyWebhookSignature(req: Request): Promise<{ ok: boolean; event?: string; data?: any }>;

  /** Returns available instalment options for a given amount */
  getInstallmentOptions(amountAED: number, currency?: string): Promise<{
    plans: Array<{
      tenorMonths: number;
      monthlyAmount: number;
      totalAmount: number;
      aprPercent?: number;
    }>;
  }>;
}

export async function getAdapter(code: ProviderCode): Promise<ProviderAdapter> {
  switch (code) {
    case 'tabby':  return (await import('./tabby')).default;
    case 'tamara': return (await import('./tamara')).default;
    case 'cashew': return (await import('./cashew')).default;
    default: throw new Error(`Unknown provider: ${code}`);
  }
}

/** Helper for monthly price examples (UI only) */
export function calcMonthly(amountAED: number, tenor: number) {
  return Math.ceil(amountAED / Math.max(tenor, 1));
}